import React, { useCallback, useEffect, useRef } from 'react';

import noop from 'ui-components/default-props/noop';

import { HIGH_PRIORITY_QUEUE, LOW_PRIORITY_QUEUE } from './constants';
import closest from '../../services/closest-service';

/**
 * @typedef {import('./global-event-listener-types.d.ts').GlobalEventListenerContextProviderComponent} GlobalEventListenerContextProviderComponent
 * @typedef {import('./global-event-listener-types.d.ts').GlobalEventListenerContextValue} GlobalEventListenerContextValue
 * @typedef {import('./global-event-listener-types.d.ts').GlobalEventListenerContextEventListener} GlobalEventListenerContextEventListener
 * @typedef {import('./global-event-listener-types.d.ts').ListenerTypes} ListenerTypes
 */

/** @type {GlobalEventListenerContextValue} */
const defaultValue = {
  focus: noop,
  listeners: { current: null },
  subscribe: () => noop,
  isDefaultValue: true,
};

/** @type {React.Context<GlobalEventListenerContextValue>} */
const Context = React.createContext(defaultValue);
const { Consumer, Provider } = Context;

const emptyArray = [];
/** @type {ListenerTypes[]} */
const listenerTypes = ['onMouseDown', 'onMouseUp', 'onMouseMove', 'onKeyDown', 'onClick', 'onResize', 'onScroll'];

let roots = [];

const isTopmostRoot = (ref) => roots[roots.length - 1].root === ref;
const isWindowFocused = () => {
  try {
    if (global.window === global.window.top) {
      return true;
    }
    const activeEl = global.window.top.document.activeElement;
    const isIFrame = activeEl && activeEl.nodeName === 'IFRAME';
    if (!isIFrame) {
      return false;
    }
    const frame = /** @type {HTMLIFrameElement} */ (activeEl);
    return frame.contentWindow === global.window;
  } catch {
    // In case of cross-origin iframe security errors, we are not the currently-focused iframe, so return false
    return false;
  }
};

/**
 * @type {GlobalEventListenerContextProviderComponent}
 */
const GlobalEventListenerContextProvider = React.forwardRef(({ children }, incomingRef) => {
  const ref = useRef();

  const actualRef = /** @type {React.MutableRefObject<HTMLElement>} */ (incomingRef || ref);
  /** @type {React.MutableRefObject<GlobalEventListenerContextEventListener>} */
  const listeners = useRef(
    listenerTypes.reduce((eventHandlers, eventType) => {
      // Set up multiple queues of events, so that we can prioritize specific
      // event listener types to be processed before other specific types, while
      // still also allowing us to always prepend incoming event listeners to
      // the internal queue of event listeners to be processed. This way, we can
      // always allow deeper listeners to override or squash parent listeners,
      // while also allowing certain types of registrations (e.g. hotkeys) to
      // always be processed after certain other types of higher priority.
      // eslint-disable-next-line
      eventHandlers[eventType] = {
        [HIGH_PRIORITY_QUEUE]: emptyArray,
        [LOW_PRIORITY_QUEUE]: emptyArray,
      };

      return eventHandlers;
    }, /** @type {GlobalEventListenerContextEventListener} */ ({})),
  );
  const focus = useCallback(() => {
    if (actualRef.current) {
      actualRef.current.focus();
    }
  }, [actualRef]);

  useEffect(() => {
    let isMounted = true;

    const focusInHandler = (e) => {
      // Check if focus is going to Sierra chat widget
      const sierraChatContainer = global.document.querySelector('[data-sierra-chat-container]');
      if (sierraChatContainer) {
        // Check if the target is the Sierra chat container or if it has the ignore attribute
        if (e.target === sierraChatContainer || sierraChatContainer.hasAttribute('data-ignore-root-focus')) {
          return;
        }

        // Check if the target is within the Sierra chat's shadow DOM
        if (e.target.getRootNode && e.target.getRootNode() !== global.document) {
          const rootNode = e.target.getRootNode();
          if (rootNode.host === sierraChatContainer) {
            return;
          }
        }
      }

      if (
        isTopmostRoot(actualRef) &&
        isWindowFocused() &&
        // Adding an exclusion for elements specifically set to ignore the root focus mechanism
        !e.target.closest('[data-ignore-root-focus]') &&
        // Adding an exclusion for delighted so that it can keep focus when a user is typing a comment
        !e.target.className?.includes?.('delighted-web-comment-box') &&
        actualRef.current &&
        actualRef.current !== e.target &&
        !actualRef.current.contains(e.target)
      ) {
        focus();
      }
    };

    const focusOutHandler = () => {
      setTimeout(() => {
        // Check if focus went to Sierra chat widget
        const sierraChatContainer = global.document.querySelector('[data-sierra-chat-container]');
        if (sierraChatContainer) {
          // Check if activeElement is the Sierra chat container or if it has the ignore attribute
          if (global.document.activeElement === sierraChatContainer || sierraChatContainer.hasAttribute('data-ignore-root-focus')) {
            return;
          }

          // Check if we should allow Sierra chat to keep focus even if activeElement hasn't changed yet
          // This happens when clicking into shadow DOM - the setTimeout(0) may run before focus actually moves
          if (sierraChatContainer.shadowRoot && sierraChatContainer.shadowRoot.activeElement) {
            return;
          }
        }

        if (
          isMounted &&
          isTopmostRoot(actualRef) &&
          isWindowFocused() &&
          actualRef.current &&
          actualRef.current !== global.document.activeElement &&
          // Adding an exclusion for iframed-elements in the page
          global.document.activeElement.nodeName.toLowerCase() !== 'iframe' &&
          // Adding an exclusion for elements specifically set to ignore the root focus mechanism
          !global.document.activeElement.closest('[data-ignore-root-focus]') &&
          // Adding an exclusion for delighted so that it can keep focus when a user is typing a comment
          !global.document.activeElement.className?.includes?.('delighted-web-comment-box') &&
          !actualRef.current.contains(global.document.activeElement)
        ) {
          focus();
        }
      }, 0);
    };

    global.document.addEventListener('focusin', focusInHandler);
    global.document.addEventListener('focusout', focusOutHandler);

    return () => {
      isMounted = false;
      global.document.removeEventListener('focusin', focusInHandler);
      global.document.removeEventListener('focusout', focusOutHandler);
    };
  }, [actualRef, focus]);

  useEffect(() => {
    roots = roots.concat({
      activeElement: global.document.activeElement,
      root: actualRef,
    });

    if (actualRef.current && !actualRef.current.contains(global.document.activeElement)) {
      focus();
    }

    return () => {
      const currentRoot = roots.find((record) => record.root === actualRef);

      roots = roots.filter((record) => record.root !== actualRef);

      if (currentRoot.activeElement && currentRoot.activeElement !== global.document.body && closest(currentRoot.activeElement, 'html')) {
        currentRoot.activeElement.focus();
      } else {
        const parentRecord = roots[roots.length - 1];

        if (parentRecord && parentRecord.root.current) {
          parentRecord.root.current.focus();
        }
      }
    };
  }, [actualRef, focus]);

  const subscribe = useCallback((eventType, eventHandler, queue = HIGH_PRIORITY_QUEUE) => {
    if (listenerTypes.indexOf(eventType) === -1) {
      throw new Error(
        `${eventType} is not currently supported in the GlobalEventListener component, please add it to the list if you want to add support for it.`,
      );
    }

    // Create a new array of listeners to bust our cache on the root-level provider component,
    // make sure to add the new eventHandler first, because we want our deeper children to be
    // called first, so that they can stopPropagation on events from bubbling up the tree.
    const newListeners = {
      ...listeners.current,
      [eventType]: {
        ...listeners.current[eventType],
        [queue]: [].concat(eventHandler, ...listeners.current[eventType][queue]),
      },
    };

    listeners.current = newListeners;

    const unsubscribe = () => {
      const revertedListeners = listenerTypes.reduce((acc, listenerType) => {
        // eslint-disable-next-line
        acc[listenerType] = listeners.current[listenerType];

        if (listenerType !== eventType) {
          return acc;
        }

        // eslint-disable-next-line
        acc[listenerType] = {
          [HIGH_PRIORITY_QUEUE]: acc[listenerType][HIGH_PRIORITY_QUEUE].filter((handler) => handler !== eventHandler),
          [LOW_PRIORITY_QUEUE]: acc[listenerType][LOW_PRIORITY_QUEUE].filter((handler) => handler !== eventHandler),
        };

        if (acc[listenerType][HIGH_PRIORITY_QUEUE].length === 0) {
          // eslint-disable-next-line
          acc[listenerType][HIGH_PRIORITY_QUEUE] = emptyArray;
        }

        if (acc[listenerType][LOW_PRIORITY_QUEUE].length === 0) {
          // eslint-disable-next-line
          acc[listenerType][LOW_PRIORITY_QUEUE] = emptyArray;
        }

        return acc;
      }, /** @type {GlobalEventListenerContextEventListener} */ ({}));

      listeners.current = revertedListeners;
    };

    return unsubscribe;
  }, []);

  const child = React.Children.only(children);

  return (
    <Provider value={{ focus, listeners, subscribe }}>
      {React.cloneElement(child, {
        ...child.props,
        ref: actualRef,
      })}
    </Provider>
  );
});

GlobalEventListenerContextProvider.displayName = 'GlobalEventListenerContextProvider';

export default Context;

export { Consumer, listenerTypes, GlobalEventListenerContextProvider as Provider };
